// src/context/OptionsContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { optionsApi } from '../api/optionsApi';
import { adminApi } from '../api/adminApi';
import { useAuth } from './AuthContext';

const OptionsContext = createContext();

export const useOptions = () => {
  const context = useContext(OptionsContext);
  if (!context) {
    throw new Error('useOptions must be used within an OptionsProvider');
  }
  return context;
};

export const OptionsProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const fetchAllOptions = async () => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const isAdmin = user?.role === 'admin';

      if (isAdmin) {
        const [categoriesRes, sizesRes, colorsRes, badgesRes] = await Promise.all([
          adminApi.getCategories(true),
          adminApi.getSizes(true),
          adminApi.getColors(true),
          adminApi.getBadges(true),
        ]);

        setCategories(categoriesRes?.categories || []);
        setSizes(sizesRes?.sizes || []);
        setColors(colorsRes?.colors || []);
        setBadges(badgesRes?.badges || []);
      } else {
        const response = await optionsApi.getAllOptions();
        setCategories(response?.categories || []);
        setSizes(response?.sizes || []);
        setColors(response?.colors || []);
        setBadges(response?.badges || []);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
      
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to load options');
      }

      // Fallback to public API
      try {
        const response = await optionsApi.getAllOptions();
        setCategories(response?.categories || []);
        setSizes(response?.sizes || []);
        setColors(response?.colors || []);
        setBadges(response?.badges || []);
      } catch (fallbackErr) {
        console.error('Fallback failed:', fallbackErr);
        setCategories([]);
        setSizes([]);
        setColors([]);
        setBadges([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOptions();
  }, [user?.role]);

  const refreshOptions = () => {
    fetchAllOptions();
  };

  const value = {
    categories,
    sizes,
    colors,
    badges,
    loading,
    error,
    refreshOptions,
    fetchAllOptions,
  };

  return (
    <OptionsContext.Provider value={value}>
      {children}
    </OptionsContext.Provider>
  );
};